import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/app/Logo";
import { toast } from "sonner";

const SPECIALTIES = [
  "Médecine générale", "Cardiologie", "Pédiatrie", "Dermatologie", "Gynécologie",
  "Psychiatrie", "Ophtalmologie", "ORL", "Dentaire", "Kinésithérapie",
  "Sage-femme", "Infirmier", "Ostéopathie",
];

export default function Register() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "patient";
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    role: initialRole,
    email: "", password: "", name: "", phone: "",
    specialty: "Médecine générale", address: "", city: "",
    lat: "", lng: "",
    consultation_duration: 30,
    pharmacy_name: "", delivery_enabled: false,
    bio: "",
    // Consentements RGPD séparés par finalité (US-01 CDC v1.0)
    consent_data_processing: false,
    consent_communications: false,
    consent_third_party_share: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation indisponible");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set("lat", pos.coords.latitude);
        set("lng", pos.coords.longitude);
        toast.success("Position détectée");
      },
      () => toast.error("Impossible d'obtenir la position")
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    // Validation RGPD — finalité 1 obligatoire (traitement données de santé)
    if (!form.consent_data_processing) {
      setLoading(false);
      return setError("Vous devez accepter le traitement de vos données de santé pour créer un compte (RGPD).");
    }
    try {
      const payload = { ...form };
      payload.lat = payload.lat === "" ? null : parseFloat(payload.lat);
      payload.lng = payload.lng === "" ? null : parseFloat(payload.lng);
      payload.consultation_duration = parseInt(payload.consultation_duration, 10) || 30;
      payload.consultation_fee = parseInt(payload.consultation_fee, 10) || 25;
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      toast.success("Compte créé avec succès");
      navigate("/dashboard");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="flex items-center mb-8" data-testid="register-back-home">
          <Logo h={36} />
        </Link>

        <div className="bg-white rounded-xl border border-slate-200 p-8 sm:p-10">
          <h1 className="font-display text-3xl font-medium tracking-tight">Créer un compte</h1>
          <p className="text-slate-500 mt-2">Quelques informations pour commencer.</p>

          {/* Role selector */}
          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { v: "patient", label: "Patient" },
              { v: "doctor", label: "Professionnel" },
              { v: "pharmacy", label: "Pharmacie" },
            ].map((r) => (
              <button
                type="button"
                key={r.v}
                onClick={() => set("role", r.v)}
                data-testid={`register-role-${r.v}`}
                className={`p-4 rounded-md border text-sm font-medium transition ${
                  form.role === r.v
                    ? "border-[#0FACD7] bg-cyan-50 text-[#0FACD7]"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {error && (
            <Alert variant="destructive" className="mt-6">
              <AlertDescription data-testid="register-error">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label>Nom complet</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label>Mot de passe</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-11" data-testid="register-password-input" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 6 12 34 56 78" className="mt-1.5 h-11" data-testid="register-phone-input" />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 h-11" data-testid="register-city-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Adresse</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11" data-testid="register-address-input" />
            </div>
            <div>
              <Label>Latitude</Label>
              <Input value={form.lat} onChange={(e) => set("lat", e.target.value)} className="mt-1.5 h-11" data-testid="register-lat-input" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input value={form.lng} onChange={(e) => set("lng", e.target.value)} className="mt-1.5 h-11" data-testid="register-lng-input" />
            </div>
            <div className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={useMyLocation} data-testid="register-geoloc-btn" className="text-sm">
                Utiliser ma position actuelle
              </Button>
            </div>

            {form.role === "doctor" && (
              <>
                <div>
                  <Label>Spécialité</Label>
                  <Select value={form.specialty} onValueChange={(v) => set("specialty", v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="register-specialty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Durée de consultation</Label>
                  <Select value={String(form.consultation_duration)} onValueChange={(v) => set("consultation_duration", v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="register-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>N° RPPS <span className="text-xs text-slate-400">(11 chiffres)</span></Label>
                  <Input value={form.rpps} onChange={(e) => set("rpps", e.target.value)} placeholder="10000123456" className="mt-1.5 h-11 font-mono" data-testid="register-rpps-input" />
                </div>
                <div>
                  <Label>Tarif consultation (€)</Label>
                  <Input type="number" min="0" value={form.consultation_fee} onChange={(e) => set("consultation_fee", e.target.value)} className="mt-1.5 h-11" data-testid="register-fee-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Bio professionnelle</Label>
                  <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} className="mt-1.5" rows={3} data-testid="register-bio-input" />
                </div>
              </>
            )}

            {form.role === "pharmacy" && (
              <>
                <div className="sm:col-span-2">
                  <Label>Nom de la pharmacie</Label>
                  <Input value={form.pharmacy_name} onChange={(e) => set("pharmacy_name", e.target.value)} className="mt-1.5 h-11" data-testid="register-pharmacy-name-input" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <input type="checkbox" id="delivery" checked={form.delivery_enabled} onChange={(e) => set("delivery_enabled", e.target.checked)} data-testid="register-delivery-checkbox" />
                  <Label htmlFor="delivery" className="cursor-pointer">Activer l'option Livraison Premium</Label>
                </div>
              </>
            )}

            <div className="sm:col-span-2 mt-4 border-t pt-5 space-y-3">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-600">Vos consentements (RGPD)</div>
              <p className="text-xs text-slate-500 leading-relaxed">Conformément au RGPD et à la loi Informatique et Libertés, votre consentement nous est demandé pour chaque finalité distincte. Vous pouvez les modifier ou les révoquer à tout moment depuis « Mes données ».</p>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:border-[#618DB8] transition" htmlFor="consent-data">
                <input
                  type="checkbox"
                  id="consent-data"
                  required
                  checked={form.consent_data_processing}
                  onChange={(e) => set("consent_data_processing", e.target.checked)}
                  className="mt-0.5 accent-[#618DB8]"
                  data-testid="consent-data-processing"
                />
                <span className="text-sm text-slate-700 leading-snug">
                  <strong>Traitement de mes données de santé</strong> <span className="text-red-500">*</span><br />
                  <span className="text-xs text-slate-500">J'accepte que Doctobook traite mes données médicales pour fournir le service (compte, profil, échanges sécurisés, dossier partagé). Hébergement certifié HDS en France. Finalité obligatoire au service.</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:border-[#618DB8] transition" htmlFor="consent-communications">
                <input
                  type="checkbox"
                  id="consent-communications"
                  checked={form.consent_communications}
                  onChange={(e) => set("consent_communications", e.target.checked)}
                  className="mt-0.5 accent-[#618DB8]"
                  data-testid="consent-communications"
                />
                <span className="text-sm text-slate-700 leading-snug">
                  <strong>Communications & newsletters</strong> <span className="text-xs text-slate-400">(facultatif)</span><br />
                  <span className="text-xs text-slate-500">J'accepte de recevoir par email des conseils santé, des actualités produit et des invitations à des événements. Désinscription en 1 clic.</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:border-[#618DB8] transition" htmlFor="consent-third-party">
                <input
                  type="checkbox"
                  id="consent-third-party"
                  checked={form.consent_third_party_share}
                  onChange={(e) => set("consent_third_party_share", e.target.checked)}
                  className="mt-0.5 accent-[#618DB8]"
                  data-testid="consent-third-party"
                />
                <span className="text-sm text-slate-700 leading-snug">
                  <strong>Partage avec partenaires de soin</strong> <span className="text-xs text-slate-400">(facultatif)</span><br />
                  <span className="text-xs text-slate-500">J'accepte que mes données puissent être partagées avec mes professionnels de santé désignés (médecin traitant, pharmacien) pour assurer la continuité des soins. Révocable document par document.</span>
                </span>
              </label>

              <p className="text-[11px] text-slate-400 leading-relaxed">
                En créant votre compte, vous acceptez nos{" "}
                <a href="/legal/cgu" target="_blank" rel="noreferrer" className="underline text-[#618DB8]">Conditions Générales</a>
                {" "}et notre{" "}
                <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline text-[#618DB8]">Politique de confidentialité</a>.
                Pour exercer vos droits RGPD (accès, rectification, effacement, portabilité, opposition), contactez{" "}
                <a href="mailto:dpo@doctobook.com" className="underline text-[#618DB8]">dpo@doctobook.com</a>.
              </p>
            </div>

            <div className="sm:col-span-2 mt-2">
              <Button type="submit" disabled={loading || !form.consent_data_processing} className="w-full h-11 btn-primary text-white" data-testid="register-submit-btn">
                {loading ? "Création…" : "Créer mon compte"}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Déjà inscrit ? <Link to="/login" className="text-[#0FACD7] font-bold hover:text-[#FF7A5C]" data-testid="register-to-login-link">Connexion</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
